import { useEffect, useState } from 'react';

interface Activity {
  id: string;
  activityName: string;
  activityType: string;
  scheduledDate: string;
  location: string;
  assignedTo: string;
  status: string;
  description: string;
}

export default function UpcomingMeetings() {
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    const loadActivities = () => {
      const storedActivities: Activity[] = JSON.parse(localStorage.getItem('farmingActivities') || '[]');
      const upcomingActivities = storedActivities
        .filter(a => new Date(a.scheduledDate) >= new Date())
        .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
        .slice(0, 4);
      setActivities(upcomingActivities);
    };

    loadActivities();

    const handleStorageChange = () => {
      loadActivities();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('farmingActivitiesUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('farmingActivitiesUpdated', handleStorageChange);
    };
  }, []);

  const getActivityIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'planting':
        return 'ri-plant-line';
      case 'harvesting':
        return 'ri-scissors-cut-line';
      case 'irrigation':
        return 'ri-drop-line';
      case 'fertilization':
        return 'ri-flask-line';
      case 'pest control':
        return 'ri-bug-line';
      case 'maintenance':
        return 'ri-tools-line';
      default:
        return 'ri-calendar-event-line';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'planting':
        return 'bg-green-100 text-green-600';
      case 'harvesting':
        return 'bg-orange-100 text-orange-600';
      case 'irrigation':
        return 'bg-blue-100 text-blue-600';
      case 'fertilization':
        return 'bg-purple-100 text-purple-600';
      case 'pest control':
        return 'bg-red-100 text-red-600';
      case 'maintenance':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-teal-100 text-teal-600';
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-gray-900">Upcoming Farming Activities</h2>
        <button 
          onClick={() => window.REACT_APP_NAVIGATE('/dashboard/farming')}
          className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
        >
          View All
        </button>
      </div>

      <div className="space-y-4">
        {activities.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No upcoming activities</p>
        ) : (
          activities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-4 p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
              <div className={`w-12 h-12 ${getActivityColor(activity.activityType)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <i className={`${getActivityIcon(activity.activityType)} text-xl w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{activity.activityName}</h3>
                <p className="text-xs text-gray-600 mb-2">{activity.description}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                    {activity.scheduledDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line w-3 h-3 flex items-center justify-center"></i>
                    {activity.location}
                  </span>
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded whitespace-nowrap">
                {activity.activityType}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
